import { useEffect, useMemo, useState } from 'react';
import { Button, Col, Dropdown, Form, Row } from 'react-bootstrap';
import { ChevronDown, Search, XCircle } from 'react-bootstrap-icons';

import { ESTADOS_VENCIMIENTO } from '../../constants/partidas';
import ResponsiveFilterCard from '../ResponsiveFilterCard';

const MultiSelect = ({ label, options, value, onChange, getValue, getLabel }) => {
  const [search, setSearch] = useState('');
  const selected = new Set((value || []).map(String));
  const visibleOptions = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('es');
    if (!term) return options;
    return options.filter((option) =>
      String(getLabel(option)).toLocaleLowerCase('es').includes(term)
    );
  }, [getLabel, options, search]);

  const toggle = (option) => {
    const optionValue = getValue(option);
    const stringValue = String(optionValue);
    onChange(
      selected.has(stringValue)
        ? value.filter((item) => String(item) !== stringValue)
        : [...value, optionValue]
    );
  };

  return (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      <Dropdown autoClose='outside' className='partidas-multiselect'>
        <Dropdown.Toggle variant='light'>
          <span className='partidas-multiselect-selected' title={value.length ? value.map((item) => getLabel(options.find((option) => String(getValue(option)) === String(item)) ?? item)).join(', ') : 'Todos'}>
            {value.length
              ? value.map((item) => getLabel(options.find((option) => String(getValue(option)) === String(item)) ?? item)).join(', ')
              : 'Todos'}
          </span>
          <ChevronDown />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <div className='partidas-multiselect-search' onClick={(e) => e.stopPropagation()}>
            <Form.Control
              size='sm'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder='Buscar...'
              aria-label={`Buscar ${label}`}
            />
          </div>
          <div className='partidas-multiselect-options'>
            {visibleOptions.length ? (
              visibleOptions.map((option) => {
                const optionValue = getValue(option);
                return (
                  <Form.Check
                    key={String(optionValue)}
                    type='checkbox'
                    id={`${label}-${String(optionValue)}`}
                    label={getLabel(option)}
                    checked={selected.has(String(optionValue))}
                    onChange={() => toggle(option)}
                    onClick={(event) => event.stopPropagation()}
                  />
                );
              })
            ) : (
              <span className='partidas-multiselect-empty'>Sin coincidencias</span>
            )}
          </div>
          {value.length > 0 && (
            <Button
              variant='link'
              size='sm'
              className='partidas-multiselect-clear'
              onClick={() => onChange([])}
            >
              Limpiar selección
            </Button>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </Form.Group>
  );
};

const lastDayOfMonth = (year, month) =>
  new Date(Number(year), Number(month), 0).getDate();

export default function PartidasFiltros({ filters, options, onApply, onClear }) {
  const [draft, setDraft] = useState(filters);
  const [dateMode, setDateMode] = useState('range');
  const [month, setMonth] = useState(filters.fechaDesde?.slice(0, 7) || '');

  useEffect(() => setDraft(filters), [filters]);
  useEffect(() => {
    if (!filters.fechaDesde && !filters.fechaHasta) setMonth('');
  }, [filters.fechaDesde, filters.fechaHasta]);

  const update = (field, value) => setDraft((current) => ({ ...current, [field]: value }));
  const invalidRange =
    draft.fechaDesde && draft.fechaHasta && draft.fechaDesde > draft.fechaHasta;
  const activeCount = [
    draft.busqueda,
    draft.productos?.length,
    draft.niveles?.length,
    draft.proveedores?.length,
    draft.estados?.length,
    draft.fechaDesde,
    draft.fechaHasta,
  ].filter(Boolean).length;

  const handleMonth = (value) => {
    setMonth(value);
    const [year, monthNumber] = value.split('-');
    if (!year || !monthNumber) {
      setDraft((current) => ({ ...current, fechaDesde: '', fechaHasta: '' }));
      return;
    }
    setDraft((current) => ({
      ...current,
      fechaDesde: `${value}-01`,
      fechaHasta: `${value}-${String(lastDayOfMonth(year, monthNumber)).padStart(2, '0')}`,
    }));
  };

  const handleClear = () => {
    const cleared = onClear();
    if (cleared) setDraft(cleared);
    setMonth('');
    setDateMode('range');
  };

  return (
    <ResponsiveFilterCard
      className='partidas-filters-card'
      title='Filtros de partidas'
      activeCount={activeCount}
    >
      <div className='crm-filter-heading partidas-section-heading'>
        <span>Consulta</span>
        <h2>Filtros de búsqueda</h2>
      </div>

      <div className='partidas-filter-content'>
        <Row className='partidas-filter-grid g-2'>
          <Col xs={12} lg={5}>
            <Form.Group>
              <Form.Label>Buscar</Form.Label>
              <div className='partidas-search-input'>
                <Search />
                <Form.Control
                  value={draft.busqueda}
                  onChange={(event) => update('busqueda', event.target.value)}
                  placeholder='Artículo, código, proveedor o partida...'
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !invalidRange) {
                      event.preventDefault();
                      onApply(draft);
                    }
                  }}
                />
              </div>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <MultiSelect
              label='Producto(s)'
              options={options.productos || []}
              value={draft.productos}
              onChange={(value) => update('productos', value)}
              getValue={(option) => option}
              getLabel={(option) => option}
            />
          </Col>
          <Col xs={12} sm={6} lg={2}>
            <MultiSelect
              label='Línea (Nivel 1)'
              options={options.niveles || []}
              value={draft.niveles}
              onChange={(value) => update('niveles', value)}
              getValue={(option) => option}
              getLabel={(option) => option}
            />
          </Col>
          <Col xs={12} lg={2}>
            <MultiSelect
              label='Proveedor(es)'
              options={options.proveedores || []}
              value={draft.proveedores}
              onChange={(value) => update('proveedores', value)}
              getValue={(option) => option}
              getLabel={(option) => option}
            />
          </Col>

          <Col xs={12} md={4} lg={3}>
            <MultiSelect
              label='Estado de vencimiento'
              options={ESTADOS_VENCIMIENTO}
              value={draft.estados}
              onChange={(value) => update('estados', value)}
              getValue={(option) => option.value}
              getLabel={(option) => option.label}
            />
          </Col>
          <Col xs={12} md={4} lg={3}>
            <Form.Group>
              <Form.Label>Tipo de fecha</Form.Label>
              <Form.Select
                value={dateMode}
                onChange={(event) => setDateMode(event.target.value)}
              >
                <option value='range'>Por rango</option>
                <option value='month'>Por mes completo</option>
              </Form.Select>
            </Form.Group>
          </Col>
          {dateMode === 'month' ? (
            <Col xs={12} md={4} lg={3}>
              <Form.Group>
                <Form.Label>Mes</Form.Label>
                <Form.Control
                  type='month'
                  value={month}
                  onChange={(event) => handleMonth(event.target.value)}
                />
              </Form.Group>
            </Col>
          ) : (
            <>
              <Col xs={6} md={4} lg={3}>
                <Form.Group>
                  <Form.Label>Desde</Form.Label>
                  <Form.Control
                    type='date'
                    value={draft.fechaDesde}
                    onChange={(event) => update('fechaDesde', event.target.value)}
                    isInvalid={invalidRange}
                  />
                </Form.Group>
              </Col>
              <Col xs={6} md={4} lg={3}>
                <Form.Group>
                  <Form.Label>Hasta</Form.Label>
                  <Form.Control
                    type='date'
                    value={draft.fechaHasta}
                    onChange={(event) => update('fechaHasta', event.target.value)}
                    isInvalid={invalidRange}
                  />
                </Form.Group>
              </Col>
            </>
          )}
        </Row>

        {invalidRange && (
          <p className='partidas-filter-error'>La fecha Desde no puede ser posterior a Hasta.</p>
        )}

        <div className='crm-filter-actions-row partidas-filter-actions-row'>
          <Button variant='outline-primary' onClick={handleClear}>
            <XCircle /> Limpiar filtros
          </Button>
          <Button
            variant='primary'
            disabled={invalidRange}
            onClick={() => {
              onApply(draft);
            }}
          >
            <Search /> Aplicar filtros
          </Button>
        </div>
      </div>
    </ResponsiveFilterCard>
  );
}
